import { useState } from 'react';
import { Users, Plus } from 'lucide-react';
import { api } from '../api';
import { useLoad } from '../hooks/useLoad';
import { useOrg } from '../auth/OrgContext';
import { PageHeader, Card, Badge, EmptyState } from '../components/ui';
import { ImportPeopleModal } from '../components/ImportPeopleModal';

// "People" is a nav LABEL only — the canonical entity stays Recipient (Amendment 02 §8).
interface Person {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: string | null;
  location: string | null;
  verification: { state: 'valid' | 'invalid' | 'unreachable'; checked_at: string } | null;
}

const VERIFY_LABEL: Record<string, string> = {
  valid: 'Verified',
  invalid: 'Invalid',
  unreachable: 'Unreachable',
};

export function PeoplePage() {
  const { withOrg, selectedOrgId } = useOrg();
  const { data, error, reload } = useLoad(
    () => api.get<{ data: Person[] }>(withOrg('/v1/recipients')),
    [selectedOrgId],
  );
  const [verifying, setVerifying] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const people = data?.data ?? [];

  async function verify(id: string) {
    setVerifying(id);
    try {
      await api.post(`/v1/recipients/${id}/verify`);
      reload();
    } finally {
      setVerifying(null);
    }
  }

  const fullName = (p: Person) => [p.first_name, p.last_name].filter(Boolean).join(' ') || '—';

  return (
    <div>
      <PageHeader
        title="People"
        subtitle="Durable recipients synced across your organization."
        actions={
          <button
            onClick={() => setImporting(true)}
            disabled={!selectedOrgId}
            title={selectedOrgId ? 'Import people' : 'Select an organization first'}
            className="flex items-center gap-1.5 rounded-input bg-ink px-3 py-2 text-sm font-medium text-paper disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" /> Import
          </button>
        }
      />

      {importing && selectedOrgId && (
        <ImportPeopleModal
          orgId={selectedOrgId}
          onClose={() => setImporting(false)}
          onImported={reload}
        />
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-line bg-ink/[0.03] px-3 py-2 text-sm text-muted">
          {error}
        </div>
      )}

      <Card className="overflow-hidden">
        {people.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No people yet"
            hint="Import recipients (CSV, manual, or a directory integration) to populate this list."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-line text-left text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Email</th>
                <th className="px-5 py-3">Role</th>
                <th className="px-5 py-3">Location</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Simulations</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {people.map((p) => (
                <tr key={p.id}>
                  <td className="px-5 py-3 text-ink">{fullName(p)}</td>
                  <td className="px-5 py-3 font-mono text-xs text-muted">{p.email}</td>
                  <td className="px-5 py-3 text-muted">{p.role ?? '—'}</td>
                  <td className="px-5 py-3 text-muted">{p.location ?? '—'}</td>
                  <td className="px-5 py-3">
                    {p.verification ? (
                      <Badge tone="outline">{VERIFY_LABEL[p.verification.state] ?? '—'}</Badge>
                    ) : (
                      <span className="text-xs text-muted">unverified</span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <Badge tone="muted">Included</Badge>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => verify(p.id)}
                      disabled={verifying === p.id}
                      className="rounded-input border border-line px-2.5 py-1 text-xs font-medium text-ink transition-colors hover:bg-ink/[0.04] disabled:opacity-50"
                    >
                      {verifying === p.id ? 'Verifying…' : 'Verify'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <p className="mt-3 text-xs text-muted">
        Verify checks email syntax + mail-server (MX) reachability. It sends nothing — safe in
        sandbox mode. Inclusion in simulations is managed under Groups.
      </p>
    </div>
  );
}
